import { Formik, Form, Field, FieldArray } from 'formik';
import React, { useState, useMemo, useEffect } from 'react'
import { CampaignSchema } from '../../../schemas/CampaignSchema';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosService from '../../../utils/AxiosService';
import Input from "../../FormComponents/Input"
import ReactMultiSelect from '../../FormComponents/ReactMultiSelect';
import { CheckLg, Image, Person, Plus, Trash } from 'react-bootstrap-icons';
import InstagramButton from '../Shared/ProviderButtons/InstagramButton';
import TikTokButton from '../Shared/ProviderButtons/TikTokButton';
import YouTubeButton from '../Shared/ProviderButtons/YouTubeButton';
import LinkedInButton from '../Shared/ProviderButtons/LinkedInButton';
import PinterestButton from '../Shared/ProviderButtons/PinterestButton';
import PodcastButton from '../Shared/ProviderButtons/PodcastButton'
import OtherButton from '../Shared/ProviderButtons/OtherButton';
import FileDropzone from '../../FormComponents/FileDropzone';
import { toastSuccess, toastError } from '../../FormComponents/Toasts';
import ReactSelect from '../../FormComponents/ReactSelect';
import Divider from '../Campaign/CampaignPageParts/SIdebarParts/Divider';
import ClientContacts from './NewCampaignParts/ClientContacts';
import CurrencySelect from '../../FormComponents/CurrencySelect';
import Button from '../../Shared/Button';
import Checkbox from '../../FormComponents/Checkbox';

const EMPTY_CUSTOM_FIELD = { label: '', field_type: 'note', advertiser_fee_enabled: true, ksa_enabled: true }

const NewCampaign = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [src, setSrc] = useState(null)
  const [projectManagers, setProjectManagers] = useState([])
  const [clientOptions, setClientOptions] = useState([])
  const [selectedClientId, setSelectedClientId] = useState(null)
  const [folderOptions, setFolderOptions] = useState([])
  const [selectedCollaborators, setSelectedCollaborators] = useState([])
  const [showCreate, setShowCreate] = useState(false)


  const [clientContacts, setClientContacts] = useState([])
  const addToClientContacts = newClient => setClientContacts([...clientContacts, newClient])
  const updateClientContacts = (updatedContact, index) => {
    const newClientContactsList = clientContacts.map((contact, i) => i === index ? updatedContact : contact)
    setClientContacts(newClientContactsList)
  }
  const deleteClientContact = (index) => {
    const newClientContactsList = clientContacts.filter((_, i) => i !== index)
    setClientContacts(newClientContactsList)
  }

  const collaboratorIds = useMemo(() => selectedCollaborators.reduce((ids, collaborator) => [...ids, collaborator.id], []), [selectedCollaborators, selectedCollaborators.length])
  const collaboratorOptions = useMemo(() => projectManagers.reduce((options, projectManager) => {
    if (!collaboratorIds.includes(projectManager.id)) options.push({ label: [projectManager.first_name, projectManager.last_name].filter(Boolean).join(" ") || projectManager.email, avatar: projectManager.avatar?.url , value: projectManager.id })
    return options
  }, []), [projectManagers, collaboratorIds, collaboratorIds.length])

  const getProjectManagers = () => {
    axiosService.get('/api/project_manager/project_managers')
      .then(projectManagers => {
        setProjectManagers(projectManagers)
      })
  }

  const getClients = () => {
    axiosService.get('/api/project_manager/clients')
      .then((clients) => {
        setClientOptions(clients.map(client => ({label: client.name, value: client.id })))
      })
      .catch(err => console.log(err))
  }

  const getFolders = (clientId) => {
    axiosService.get(`/api/project_manager/clients/${clientId}/folders`)
      .then((folders) => {
        console.log(folders)
        setFolderOptions(folders.map(folder => ({ label: folder.name, value: folder.id })))
      })
  }

  useEffect(() => {
    getProjectManagers()
    getClients()
    if (location.state) getFolders(location.state.client_id)
  }, [])


  const handleSubmit = (campaign) => {
    const formData = new FormData()
    const customFieldsAttributes = (campaign.custom_fields || []).reduce((result, field) => {
      const label = (field?.label || '').trim()
      if (!label) return result

      result.push({
        label,
        field_type: field.field_type || 'note',
        advertiser_fee_enabled: !!field.advertiser_fee_enabled,
        ksa_enabled: !!field.ksa_enabled
      })
      return result
    }, [])

    formData.append('campaign[name]', campaign.name)
    formData.append('campaign[project_number]', campaign.project_number)
    formData.append('campaign[project_manager_id]', campaign.project_manager_id)
    formData.append('campaign[start_date]', campaign.start_date)
    formData.append('campaign[end_date]', campaign.end_date)
    formData.append('campaign[gross_reach_target]', campaign.gross_reach_target)
    formData.append('campaign[net_reach_target]', campaign.net_reach_target)
    formData.append('campaign[impressions_target]', campaign.impressions_target)
    formData.append('campaign[influencer_target]', campaign.influencer_target)
    formData.append('campaign[action_count_target]', campaign.action_count_target)
    formData.append('campaign[influencer_budget]', campaign.influencer_budget)
    formData.append('campaign[social_budget]', campaign.social_budget)
    formData.append('campaign[media_budget]', campaign.media_budget)
    formData.append('campaign[travel_cost_enabled]', campaign.travel_cost_enabled ? '1' : '0')
    formData.append('campaign[travel_cost_target]', campaign.travel_cost_target)
    formData.append('campaign[attendance_cost_enabled]', campaign.attendance_cost_enabled ? '1' : '0')
    formData.append('campaign[attendance_cost_target]', campaign.attendance_cost_target)
    formData.append('campaign[theme_color]', campaign.theme_color)
    formData.append('campaign[currency]', campaign.currency)
    formData.append('campaign[currency_symbol]', campaign.currency_symbol)
    if (campaign.image) formData.append('campaign[image]', campaign.image)
    campaign.platforms.forEach(platform => formData.append('campaign[platforms][]', platform))
    customFieldsAttributes.forEach((field, index) => {
      formData.append(`campaign[campaign_custom_fields_attributes][${index}][label]`, field.label)
      formData.append(`campaign[campaign_custom_fields_attributes][${index}][field_type]`, field.field_type)
      formData.append(`campaign[campaign_custom_fields_attributes][${index}][advertiser_fee_enabled]`, field.advertiser_fee_enabled ? '1' : '0')
      formData.append(`campaign[campaign_custom_fields_attributes][${index}][ksa_enabled]`, field.ksa_enabled ? '1' : '0')
    })

    if (campaign.is_new_client === false) {
      formData.append('campaign[client_id]', campaign.client_id)
      formData.append('campaign[folder_id]', campaign.folder_id)
    } else {
      formData.append('campaign[client_attributes][name]', campaign.new_client_name)
      formData.append('campaign[client_attributes][theme_color]', campaign.client_theme_color)
      if (campaign.client_image) formData.append('campaign[client_attributes][image]', campaign.client_image)
    }


    campaign?.client_collaborators.forEach(collab => formData.append('campaign[client_attributes][collaborator_ids][]', collab.value))

    if (clientContacts.length !== 0) {
      clientContacts.forEach((contact, index) => {
        formData.append(`campaign[client_attributes][client_contacts_attributes][${index}][name]`, contact.name)
        formData.append(`campaign[client_attributes][client_contacts_attributes][${index}][email]`, contact.email)
        formData.append(`campaign[client_attributes][client_contacts_attributes][${index}][phone]`, contact.phone)
        formData.append(`campaign[client_attributes][client_contacts_attributes][${index}][notes]`, contact.notes)
        if (contact.image) formData.append(`campaign[client_attributes][client_contacts_attributes][${index}][avatar]`, contact.image)
      })
    }

    axiosService.post('/api/project_manager/campaigns', formData, { headers: { "Content-type": "multipart/form-data" } })
      .then((resp) => {
        toastSuccess('Campaign successfully created!')
        navigate(`/project_manager/campaigns/${resp.id}/overview`)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const themeColors = [
    '#E54747',
    '#E59D47',
    '#E9E12F',
    '#A0E547',
    '#47E590',
    '#47E5E5',
    '#6747E5',
    '#BC47E5',
    '#E547A6'
  ]

  return (
    <div className='flex h-full w-full bg-rb-gre'>
      <div className='mx-auto max-w-screen-lg h-full pt-20'>
        <div className="min-w-[600px] h-full">
          <h1 className='font-bold text-2xl mb-5'>Campaign Setup</h1>
          <Formik
            initialValues={{
              name: '',
              client_id: location?.state?.client_id || '',
              is_new_client: false,
              project_number: '',
              project_manager_id: '',
              start_date: '',
              end_date: '',
              gross_reach_target: null,
              net_reach_target: null,
              impressions_target: null,
              action_count_target: null,
              influencer_target: null,
              influencer_budget: null,
              social_budget: null,
              media_budget: null,
              travel_cost_enabled: false,
              travel_cost_target: null,
              attendance_cost_enabled: false,
              attendance_cost_target: null,
              platforms: [],
              client_collaborators: [],
              theme_color: '',
              image: '',
              new_client_name: '',
              client_theme_color: '',
              client_image: '',
              folder_id: location?.state?.folder_id || '',
              currency: 'EUR',
              currency_symbol: '€',
              custom_fields: [{ ...EMPTY_CUSTOM_FIELD }]
            }}
            enableReinitialize
            validationSchema={CampaignSchema}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue, handleBlur, handleChange, values, touched, errors }) => {
              return (
                <Form>
                  <div className="form-item-container w-full flex flex-col gap-8">

                    <section className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <h3 className='font-bold text-lg'>General</h3>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Input error={touched.name && errors.name} noLabelPadding label="Name" placeholder="Campaign Name" name='name' onChange={handleChange} onBlur={handleBlur} value={values.name} topMargin='mt-1'/>
                        </div>
                        <div className='pt-1'>
                          <ReactSelect
                              error={touched.project_manager_id && errors.project_manager_id}
                              isClearable
                              name="project_manager_id"
                              onChange={project_manager_id => {
                                setFieldValue('project_manager_id', project_manager_id?.value, true)
                              }}
                              onBlur={handleBlur}
                              value={values.project_manager_id}
                              options={collaboratorOptions}
                              label='Owner'
                              noLabelPadding
                            />
                        </div>
                        <div className="col-span-2">
                          <Input error={touched.project_number && errors.project_number} noLabelPadding label="Project Number" name='project_number' placeholder="Moco Project ID Number" onChange={handleChange} onBlur={handleBlur} value={values.project_number} topMargin='mt-1'/>
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-rb-black dark:text-rb-dark-mode-text-grey">
                            Campaign Start Date
                          </label>
                          <input
                            type="date"
                            name="start_date"
                            value={values.start_date}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="rb-date-input h-11 w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter px-4 bg-white dark:bg-rb-dark-mode text-rb-black dark:text-rb-dark-mode-text-grey [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-0 focus:border-rb-green focus:border-2"
                          />
                          {touched.start_date && errors.start_date && (
                            <p className="text-red-500 text-[10px] mt-1 px-2">{errors.start_date}</p>
                          )}
                        </div>
                        <div>
                          <label className="block mb-1 text-sm font-medium text-rb-black dark:text-rb-dark-mode-text-grey">
                            Campaign End Date
                          </label>
                          <input
                            type="date"
                            name="end_date"
                            min={values.start_date || undefined}
                            value={values.end_date}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="rb-date-input h-11 w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter px-4 bg-white dark:bg-rb-dark-mode text-rb-black dark:text-rb-dark-mode-text-grey [color-scheme:light] dark:[color-scheme:dark] focus-visible:outline-0 focus:border-rb-green focus:border-2"
                          />
                          {touched.end_date && errors.end_date && (
                            <p className="text-red-500 text-[10px] mt-1 px-2">{errors.end_date}</p>
                          )}
                        </div>
                      </div>
                    </section>

                    <section className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <div>
                        <h3 className='font-bold text-lg'>Client</h3>
                        <p className='rb-text-secondary mb-4'>Select current, or create a new client</p>
                        <div className="flex items-center gap-3">
                          <div className='flex-1'>
                            <ReactSelect
                              error={touched.client_id && errors.client_id}
                              isClearable
                              name="client_id"
                              onChange={client_id => {
                                setSelectedClientId(client_id)
                                getFolders(client_id?.value)
                                setFieldValue('client_id', client_id?.value, true)
                              }}
                              onBlur={handleBlur}
                              value={values.client_id}
                              options={clientOptions}
                              disabled={showCreate}
                            />
                          </div>
                          <Button
                            className={`mb-2 py-2.5 px-3 ${showCreate ? 'rb-disabled' : ''}`}
                            disabled={showCreate}
                            onClick={() => {
                              values.is_new_client = true
                              values.client_id = ''
                              setFieldValue('client_id', '', true)
                              setShowCreate(true)
                            }}
                          >
                            Create new
                          </Button>
                        </div>
                      </div>
                      {
                        showCreate && (
                          <div className='mt-2'>
                            <Divider />
                            <h3 className='font-bold text-lg mt-5'>Create new client</h3>
                            <div className='mt-3'>
                              <Input error={touched.new_client_name && errors.new_client_name} noLabelPadding label="Name" placeholder="Client name" name="new_client_name" onChange={handleChange} onBlur={handleBlur} value={values.new_client_name} />
                            </div>
                            <h4 className='font-bold text-md mt-5'>Client collaborators</h4>
                            <div className="flex flex-col gap-4 items-center">
                              <div className='flex w-full mt-5 justify-center'>
                                {selectedCollaborators.map(collaborator => (
                                  <div key={collaborator.id} className="-ml-2">
                                    {collaborator?.avatar && <img className="w-9 h-9 rounded-full object-cover" src={collaborator.avatar} alt="avatar" />
                                      || <div className="w-9 h-9 bg-rb-green rounded-full text-white flex items-center justify-center border-1 border-white"><Person /></div>}
                                  </div>
                                ))}
                              </div>
                              <div className='mt-3 mx-auto w-full'>
                                <ReactMultiSelect
                                  placeholder="Select Collaborators"
                                  name="collaborators"
                                  options={collaboratorOptions}
                                  value={selectedCollaborators}
                                  hasSelectAll={false}
                                  onChange={(collaborators) => {
                                    setFieldValue('client_collaborators', collaborators)
                                    setSelectedCollaborators(collaborators)
                                  }}
                                />
                              </div>
                            </div>

                            <h4 className='font-bold text-md mt-5'>Client Color</h4>
                            <p className='text-rb-mat-grey text-md'>select a color for your client thumbnail</p>
                            <div className='flex justify-between items-center mt-5'>
                              <div className="flex px-2">
                                {
                                  themeColors.map((color, index) => (
                                    <label
                                      key={index}
                                      className="cursor-pointer h-8 w-8 rounded-full border-1 border-white -ml-2 flex justify-center items-center"
                                      style={{ backgroundColor: color }}
                                    >
                                      <Field type="radio" name="client_theme_color" value={color} className="hidden" />
                                      {values.client_theme_color === color ? <CheckLg className='text-white text-xl' /> : ''}
                                    </label>
                                  ))
                                }
                              </div>
                              <div className="h-16 w-30 rounded-md p-3 flex justify-between items-center border-1 border-rb-ligher-grey">
                                <div className="h-12 w-12 mr-2 rounded-md p-4 font-bold flex items-center justify-center border-1 border-rb-lighter-grey" style={{ backgroundColor: values.client_theme_color }}>
                                  <span>{values.new_client_name.slice(0, 2).toUpperCase() || 'TP'}</span>
                                </div>
                                <p className='w-[150px] truncate font-bold'>{values.new_client_name || 'Thumbnail Preview'}</p>
                              </div>
                            </div>

                            <div className='mt-5'>
                              <p className='text-rb-mat-grey text-md mb-3'>or select an image</p>
                              <FileDropzone
                                uploadFiles={files => {
                                  setSrc(URL.createObjectURL(files[0]))
                                  setFieldValue('client_image', files[0])
                                }}
                                icon={<Image />}
                                key='client_upload'
                              />
                              <div className={`w-full flex justify-center ${src && 'py-6' || 'py-1'}`}>
                                {src && <img className="h-20 w-20 rounded-3xl" src={src} />}
                              </div>
                            </div>
                            <ClientContacts
                              clientContacts={clientContacts}
                              addToClientContacts={addToClientContacts}
                              updateClientContacts={updateClientContacts}
                              deleteClientContact={deleteClientContact}
                            />
                            <Button
                              className='mt-2'
                              variant='subtle'
                              onClick={() => {
                                values.is_new_client = false
                                setShowCreate(false)
                              }}
                            >
                              Delete new client
                            </Button>
                          </div>
                        )
                      }
                    </section>

                    <section>
                      {
                        values.client_id && (
                        <div className="bg-white dark:bg-rb-dark-mode rounded-lg p-5 border-1 border-rb-widget-boarder dark:border-rb-dark-mode-lighter w-full">
                          <h3 className='font-bold text-lg dark:text-white'>Folder</h3>
                          <p className="text-md font-normal rb-text-secondary pb-4">Which folder will this campaign belong to?</p>
                          <div className='mt-3 w-full'>
                            <ReactSelect
                              error={touched.folder_id && errors.folder_id}
                              isClearable
                              name="folder_id"
                              onChange={folder_id => {
                                setFieldValue('folder_id', folder_id?.value, true)
                              }}
                              onBlur={handleBlur}
                              value={values.folder_id}
                              options={folderOptions}
                              disabled={showCreate}
                            />
                          </div>
                        </div>

                        )
                      }
                    </section>

                    <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <h3 className='font-bold text-lg'>Platforms</h3>
                      <p className="text-md font-normal rb-text-secondary pb-4">Select the platforms that are included in your campaign</p>
                      <div role="group" className="flex flex-wrap justify-center gap-2 mx-auto rounded-lg bg-gray-200 w-96 p-2 dark:bg-rb-dark-mode-lighter">
                        <InstagramButton
                          variant="checkbox"
                          active={values.platforms.includes('instagram')}
                          name="platforms"
                          value="instagram"
                        />
                        <TikTokButton
                          variant="checkbox"
                          active={values.platforms.includes('tiktok')}
                          name="platforms"
                          value="tiktok"
                        />
                        <YouTubeButton
                          variant="checkbox"
                          active={values.platforms.includes('youtube')}
                          name="platforms"
                          value="youtube"
                        />
                        <PinterestButton
                          variant="checkbox"
                          active={values.platforms.includes('pinterest')}
                          name="platforms"
                          value="pinterest"
                        />
                        <LinkedInButton
                          variant="checkbox"
                          active={values.platforms.includes('linkedin')}
                          name="platforms"
                          value="linkedin"
                        />
                        <PodcastButton
                          variant="checkbox"
                          active={values.platforms.includes('podcast')}
                          name="platforms"
                          value="podcast"
                        />
                        <OtherButton
                          variant="checkbox"
                          active={values.platforms.includes('other')}
                          name="platforms"
                          value="other"
                        />
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <h3 className='font-bold text-lg text-rb-black dark:text-rb-dark-mode-text-light'>Targets</h3>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <Input error={touched.gross_reach_target && errors.gross_reach_target} noLabelPadding label="Gross Reach" placeholder="Gross Reach" name="gross_reach_target" onChange={handleChange} onBlur={handleBlur} value={values.gross_reach_target} />
                        </div>
                        <div>
                          <Input error={touched.net_reach_target && errors.net_reach_target} noLabelPadding label="Net Reach" placeholder="Net Reach" name="net_reach_target" onChange={handleChange} onBlur={handleBlur} value={values.net_reach_target} />
                        </div>
                        <div>
                          <Input error={touched.impressions_target && errors.impressions_target} noLabelPadding label="Impressions" placeholder="Impressions" name="impressions_target" onChange={handleChange} onBlur={handleBlur} value={values.impressions_target} />
                        </div>
                        <div>
                          <Input
                            error={touched.influencer_target && errors.influencer_target}
                            addon={<Person />}
                            addonPosition="end"
                            noLabelPadding
                            label="Influencers"
                            placeholder="Influencer count"
                            name="influencer_target"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.influencer_target}
                          />
                        </div>
                        <div className='pt-1'>
                          <CurrencySelect
                            error={touched.currency && errors.currency}
                            isClearable
                            name="currency"
                            label="Currency"
                            onChange={currency => {
                              setFieldValue('currency', currency?.value, true)
                              setFieldValue('currency_symbol', currency?.symbol, true)
                            }}
                            onBlur={handleBlur}
                            value={values.currency}
                            noLabelPadding
                          />
                        </div>
                        <div>
                          <Input
                            addon={values.currency_symbol || '€'}
                            addonPosition="start"
                            className='px-2'
                            error={touched.influencer_budget && errors.influencer_budget}
                            noLabelPadding
                            label="Influencer Budget"
                            placeholder="Influencer budget"
                            name="influencer_budget"
                            onChange={(e) => {
                              setFieldValue('influencer_budget', e.target.value.replace(/,/g, '.'), true)
                            }}
                            onBlur={handleBlur}
                            value={values.influencer_budget}
                          />
                        </div>
                        <div>
                          <Input
                            addon={values.currency_symbol || '€'}
                            addonPosition="start"
                            className='px-2'
                            error={touched.social_budget && errors.social_budget}
                            noLabelPadding
                            label="Social Budget"
                            placeholder="Social ads budget"
                            name="social_budget"
                            onChange={(e) => {
                              setFieldValue('social_budget', e.target.value.replace(/,/g, '.'), true)
                            }}
                            onBlur={handleBlur}
                            value={values.social_budget}
                          />
                        </div>
                        <div>
                          <Input
                            addon={values.currency_symbol || '€'}
                            addonPosition="start"
                            className='px-2'
                            error={touched.media_budget && errors.media_budget}
                            noLabelPadding
                            label="Buyout Budget"
                            placeholder="Buyout budget"
                            name="media_budget"
                            onChange={(e) => {
                              setFieldValue('media_budget', e.target.value.replace(/,/g, '.'), true)
                            }}
                            onBlur={handleBlur}
                            value={values.media_budget}
                          />
                        </div>
                        <div>
                          <Input
                            error={touched.action_count_target && errors.action_count_target}
                            noLabelPadding
                            label="Action Count Target"
                            placeholder="Target number of actions"
                            name="action_count_target"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.action_count_target}
                          />
                        </div>
                      </div>

                      <div className="mt-6 pt-4 border-t-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter">
                        <p className="font-bold text-sm text-rb-black dark:text-white mb-3">Additional cost widgets</p>
                        <p className="text-sm rb-text-secondary mb-3">Enable to show a Target and Status widget for this cost, with a matching entry in the Budget Table's Sonstiges section.</p>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Checkbox
                              label="Travel Costs"
                              name="travel_cost_enabled"
                              checked={!!values.travel_cost_enabled}
                              onChange={(e) => setFieldValue('travel_cost_enabled', e.target.checked, false)}
                            />
                            {values.travel_cost_enabled && (
                              <div className="mt-2">
                                <Input
                                  addon={values.currency_symbol || '€'}
                                  addonPosition="start"
                                  className='px-2'
                                  error={touched.travel_cost_target && errors.travel_cost_target}
                                  noLabelPadding
                                  label="Travel Costs Target"
                                  placeholder="Travel costs budget"
                                  name="travel_cost_target"
                                  onChange={(e) => setFieldValue('travel_cost_target', e.target.value.replace(/,/g, '.'), true)}
                                  onBlur={handleBlur}
                                  value={values.travel_cost_target}
                                />
                              </div>
                            )}
                          </div>
                          <div>
                            <Checkbox
                              label="Event Attendance"
                              name="attendance_cost_enabled"
                              checked={!!values.attendance_cost_enabled}
                              onChange={(e) => setFieldValue('attendance_cost_enabled', e.target.checked, false)}
                            />
                            {values.attendance_cost_enabled && (
                              <div className="mt-2">
                                <Input
                                  addon={values.currency_symbol || '€'}
                                  addonPosition="start"
                                  className='px-2'
                                  error={touched.attendance_cost_target && errors.attendance_cost_target}
                                  noLabelPadding
                                  label="Event Attendance Target"
                                  placeholder="Event attendance budget"
                                  name="attendance_cost_target"
                                  onChange={(e) => setFieldValue('attendance_cost_target', e.target.value.replace(/,/g, '.'), true)}
                                  onBlur={handleBlur}
                                  value={values.attendance_cost_target}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-6 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <h3 className="font-bold text-xl text-rb-black dark:text-white mb-6">Budget Table Settings</h3>
                      <p className="font-bold text-md text-rb-black dark:text-white mb-4">Custom columns</p>
                      <FieldArray name="custom_fields">
                        {({ remove, push }) => (
                          <div className="flex flex-col gap-4">
                            {values.custom_fields.map((field, index) => (
                              <div
                                key={`new-${index}`}
                                className="rounded-xl border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode p-4 shadow-sm"
                              >
                                <div className="flex flex-row items-end gap-3 w-full">
                                  <div className="flex-1 campaign-custom-field-type-select react-select-white-bg min-w-0">
                                    <ReactSelect
                                      label="Type"
                                      noLabelPadding
                                      isClearable={false}
                                      isSearchable={false}
                                      name={`custom_fields.${index}.field_type`}
                                      value={values.custom_fields[index].field_type}
                                      options={[
                                        { value: 'note', label: 'Note' },
                                        { value: 'numeric', label: 'Number' },
                                        { value: 'cost_item', label: 'Cost item' }
                                      ]}
                                      onChange={(option) => {
                                        setFieldValue(`custom_fields.${index}.field_type`, option?.value || 'note', false)
                                      }}
                                      onBlur={handleBlur}
                                      placeholder="Select type"
                                    />
                                  </div>

                                  <div className="flex-[1.2] min-w-0">
                                    <Input
                                      label="Label"
                                      topMargin="mt-0"
                                      noLabelPadding
                                      name={`custom_fields.${index}.label`}
                                      value={values.custom_fields[index].label}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      placeholder="Field label"
                                    />
                                  </div>

                                  <div className="flex-none">
                                    <button
                                      type="button"
                                      className="h-11 w-11 rounded-lg bg-rb-grey dark:bg-rb-dark-mode-lighter flex items-center justify-center rb-text-secondary border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter hover:bg-gray-200 dark:hover:bg-rb-dark-mode flex-shrink-0"
                                      onClick={() => {
                                        remove(index)
                                        if (values.custom_fields.length === 1) push({ ...EMPTY_CUSTOM_FIELD })
                                      }}
                                      aria-label="Delete custom field"
                                    >
                                      <Trash />
                                    </button>
                                  </div>
                                </div>

                                {values.custom_fields[index].field_type === 'cost_item' && (
                                  <div className="flex flex-row gap-6 mt-3">
                                    <Checkbox
                                      label="Apply 20% fee"
                                      name={`custom_fields.${index}.advertiser_fee_enabled`}
                                      checked={!!values.custom_fields[index].advertiser_fee_enabled}
                                      onChange={(e) => setFieldValue(`custom_fields.${index}.advertiser_fee_enabled`, e.target.checked, false)}
                                    />
                                    <Checkbox
                                      label="Apply KSA"
                                      name={`custom_fields.${index}.ksa_enabled`}
                                      checked={!!values.custom_fields[index].ksa_enabled}
                                      onChange={(e) => setFieldValue(`custom_fields.${index}.ksa_enabled`, e.target.checked, false)}
                                    />
                                  </div>
                                )}
                              </div>
                            ))}

                            <button
                              type="button"
                              className="w-full rounded-lg border-1 border-rb-lite-grey dark:border-rb-dark-mode-lighter bg-rb-grey dark:bg-rb-dark-mode-lighter py-4 px-4 font-bold text-rb-black dark:text-white flex items-center justify-center gap-2 hover:bg-gray-200 dark:hover:bg-rb-dark-mode mt-2"
                              onClick={() => push({ ...EMPTY_CUSTOM_FIELD })}
                            >
                              <Plus className="shrink-0 text-xl" />
                              Add New Field
                            </button>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                    <div className="bg-white rounded-lg p-5 border-1 border-rb-widget-boarder dark:bg-rb-dark-mode dark:border-rb-dark-mode-lighter">
                      <h3 className='font-bold text-lg'>Visuals</h3>
                      <p  className='text-rb-mat-grey text-md'>select a color for your campaign thumbnail</p>
                      <div className='flex justify-between items-center mt-5'>
                        <div>
                          <div className="flex px-2">
                            {
                              themeColors.map((color, index) => (
                                <label
                                  key={index}
                                  className="cursor-pointer h-8 w-8 rounded-full border-1 border-white -ml-2 flex justify-center items-center"
                                  style={{ backgroundColor: color }}
                                >
                                  <Field type="radio" name="theme_color" value={color} className="hidden" />
                                  { values.theme_color === color ? <CheckLg className='text-white text-xl'/> : ''}
                                </label>
                              ))
                            }
                          </div>
                        </div>
                        <div className="h-16 w-30 rounded-md p-3 flex justify-between items-center border-1 border-rb-ligher-grey">
                          <div className="h-12 w-12 mr-2 rounded-md p-4 font-bold flex items-center justify-center border-1 border-rb-lighter-grey" style={{ backgroundColor: values.theme_color }}>
                            <span>TP</span>
                          </div>
                          <p className='w-[150px] truncate font-bold'>Thumbnail preview</p>
                        </div>
                      </div>

                      <div className='mt-5'>
                        <p className='text-rb-mat-grey text-md mb-3'>or select an image</p>
                        <FileDropzone
                          uploadFiles={files => {
                            setSrc(URL.createObjectURL(files[0]))
                            setFieldValue('image', files[0])
                          }}
                          icon={<Image />}
                          key='campaign_upload'
                        />
                        <div className={`w-full flex justify-center ${src && 'py-6' || 'py-1'}`}>
                          {src && <img className="h-20 w-20 rounded-3xl" src={src} />}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mt-1">
                      <Button
                        className='mr-2'
                        onClick={() => navigate('/project_manager/campaigns')}
                        size='lg'
                        variant='cancel'
                      >
                        Cancel
                      </Button>

                      <Button
                        className="ml-auto submit"
                        type="submit"
                        variant="primary"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
      </div>
    </div>
   );
}

export default NewCampaign;
